<template>
  <div class="app">
    <div class="snow snow--fallback" aria-hidden="true"></div>
    <div class="snow" aria-hidden="true" ref="snowLayer"></div>

    <main class="page">
      <header class="header-bar">
        <div class="header-bar__content">
          <h1 class="header-bar__title">
            {{ isNew ? 'Secret Santa Link Generator' : 'Secret Santa Reveal' }}
          </h1>
          <div class="header-bar__sparkle" aria-hidden="true"></div>
          <p v-if="isNew" class="header-bar__subtitle">
            Fill in details to generate a unique reveal link.
          </p>
          <p v-else class="header-bar__subtitle">
            Tap the gift to reveal your match.
          </p>
        </div>
      </header>

      <section v-if="isNew" class="formzone">
        <form class="generator generator--card" @submit.prevent="generateLink" autocomplete="off">
          <div class="card-head">
            <div class="card-head__left">
              <div class="card-head__eyebrow">Setup</div>
              <h2 class="card-head__title">Create a reveal link</h2>
            </div>
          </div>

          <div class="section">
              <div class="form__group icon-user">
                <label for="match">Recipient’s Name (Match)</label>
                <input
                  id="match"
                  v-model="form.match"
                  placeholder="e.g. Sam Taylor"
                  required
                />
              </div>

              <div class="form__group">
                <label for="luxury">Little treat you secretly want</label>
                <input id="luxury" v-model="form.luxury" placeholder="That cosy candle, fluffy socks…" />
              </div>

              <div class="form__group">
                <label for="interests">Hobbies or gift ideas</label>
                <input id="interests" v-model="form.interests" placeholder="Coffee, football, LEGO, plants…" />
              </div>

              <div class="form__group">
                <label for="comfort">Comfort movie/TV show</label>
                <input id="comfort" v-model="form.comfort" type="text" placeholder="The Holiday, Home Alone, Elf…" />
              </div>

              <div class="form__group">
                <label for="nicheLink">Link (if niche)</label>
                <input id="nicheLink" v-model="form.nicheLink" type="url" placeholder="https://example.com/that-exact-thing" />
              </div>

              <div class="form__group">
                <label for="avoid">Things to avoid / allergies</label>
                <input id="avoid" v-model="form.avoid" placeholder="No peanuts, no scented items, etc." />
              </div>
          </div>

          <div class="actions actions--stack">
            <div class="actions__row">
              <button type="button" class="btn-generate btn-generate--large" :disabled="!form.match.trim()" @click="generateAndCopyLink" title="Generate link and copy to clipboard">
                Generate &amp; Copy
              </button>
            </div>
          </div>

          <div v-if="generatedUrl" class="output">
            <h3>Generated Link:</h3>
              <a :href="generatedUrl" target="_blank" style="color: lightblue !important;" rel="noopener">{{ generatedUrl }}</a>
              <div class="output__actions">
              </div>
              <p v-if="copiedFlash" class="copied-flash">Copied! 🎁</p>
            </div>
        </form>
      </section>

      <section v-else class="giftzone">
        <div class="box" :class="{ opened }" @click="reveal" role="button" tabindex="0" aria-label="Open gift" @keydown.enter.prevent="reveal" @keydown.space.prevent="reveal">
          <div class="box-body">
            <div class="box-lid">
              <div class="box-bowtie"></div>
            </div>
          </div>
        </div>
      </section>

      <transition name="pop-reveal">
        <div v-if="opened && !isNew" class="reveal-overlay" role="dialog" aria-modal="true">
          <section class="reveal-card">
            <div class="reveal-card__eyebrow header-like">Your match is</div>
            <div class="reveal-card__sparkle" aria-hidden="true"></div>

            <div class="reveal-card__name">{{ matchName || 'Unknown' }}</div>

            <div class="extras" v-if="hasExtras">
              <h3 class="extras__title">Additional Info</h3>
              <div class="extras__grid extras__grid--two">
                <div v-if="luxury" class="extras__row">
                  <span class="extras__label">A little treat I secretly want</span>
                  <span class="extras__value">{{ luxury }}</span>
                </div>
                <div v-if="interests" class="extras__row">
                  <span class="extras__label">Things I’m into (gift ideas)</span>
                  <span class="extras__value">{{ interests }}</span>
                </div>
                <div v-if="comfort" class="extras__row">
                  <span class="extras__label">My comfort watch</span>
                  <span class="extras__value">{{ comfort }}</span>
                </div>
                <div v-if="nicheLink" class="extras__row">
                  <span class="extras__label">It’s a bit niche - here’s a link</span>
                  <span class="extras__value">
                    <a :href="nicheLink" target="_blank" rel="noopener">Open</a>
                  </span>
                </div>
                <div v-if="avoid" class="extras__row extras__row--full">
                  <span class="extras__label">Please avoid / allergies</span>
                  <span class="extras__value">{{ avoid }}</span>
                </div>
                <div v-else-if="luxury || interests || comfort || nicheLink" class="extras__row extras__row--full">
                  <span class="extras__label">Please avoid / allergies</span>
                  <span class="extras__value extras__value--muted">I’m easy—happy with anything!</span>
                </div>
              </div>
            </div>

            <p class="reveal-card__footer">Remember to bring your gift to the Leeds Meetup! 🎄</p>
          </section>
        </div>
      </transition>
    </main>
  </div>
</template>

<script>
export default {
  name: 'App',
  data() {
    return {
      opened: false,
      matchName: '',
      note: '',
      luxury: '',
      interests: '',
      comfort: '',
      nicheLink: '',
      avoid: '',
      isNew: window.location.pathname.endsWith('/new'),

      form: {
        match: '',
        note: '',
        luxury: '',
        interests: '',
        comfort: '',
        nicheLink: '',
        avoid: ''
      },
      generatedUrl: '',
      copiedFlash: false,

      // Encryption pass (as requested)
      encryptionPass: 'tbxsecretsanta2025'
    };
  },

  computed: {
    hasExtras() {
      return !!(this.luxury || this.interests || this.comfort || this.nicheLink || this.avoid);
    }
  },

  created() {
    if (this.isNew) return;

    const params = new URLSearchParams(window.location.search);
    const token = params.get('token');

    if (token) {
      // Attempt to decrypt asynchronously. If it fails, attempt legacy base64 decode.
      this.decryptToken(token)
        .then((tokenJson) => {
          if (tokenJson) {
            this.applyTokenJson(tokenJson);
          } else {
            // try legacy base64 decode (backwards compatibility)
            const legacy = this.legacyB64decode(token);
            try {
              const parsed = JSON.parse(legacy);
              this.applyTokenJson(parsed);
            } catch (e) {
              // fallback to query params individually
              this.matchName = params.get('match') || '';
              this.note = params.get('note') || '';
              this.luxury = params.get('luxury') || '';
              this.interests = params.get('interests') || '';
              this.comfort = params.get('comfort') || '';
              this.nicheLink = params.get('nicheLink') || '';
              this.avoid = params.get('avoid') || '';
            }
          }
        })
        .catch(() => {
          // fallback: decode as legacy base64 JSON string or query params
          const legacy = this.legacyB64decode(token);
          try {
            const parsed = JSON.parse(legacy);
            this.applyTokenJson(parsed);
          } catch (e) {
            this.matchName = params.get('match') || '';
            this.note = params.get('note') || '';
            this.luxury = params.get('luxury') || '';
            this.interests = params.get('interests') || '';
            this.comfort = params.get('comfort') || '';
            this.nicheLink = params.get('nicheLink') || '';
            this.avoid = params.get('avoid') || '';
          }
        });
    } else {
      // No token: still populate from direct params if present
      this.matchName = params.get('match') || '';
      this.note = params.get('note') || '';
      this.luxury = params.get('luxury') || '';
      this.interests = params.get('interests') || '';
      this.comfort = params.get('comfort') || '';
      this.nicheLink = params.get('nicheLink') || '';
      this.avoid = params.get('avoid') || '';
    }
  },

  mounted() {
    this.$nextTick(() => this.initSnow());
    let t;
    window.addEventListener('resize', () => {
      clearTimeout(t);
      t = setTimeout(() => this.initSnow(), 150);
    });
  },

  methods: {
    applyTokenJson(obj) {
      const from = (k) => (obj && obj[k] != null ? String(obj[k]) : '');
      this.matchName = from('match') || this.matchName;
      this.note = from('note') || this.note;
      this.luxury = from('luxury') || this.luxury;
      this.interests = from('interests') || this.interests;
      this.comfort = from('comfort') || this.comfort;
      this.nicheLink = from('nicheLink') || this.nicheLink;
      this.avoid = from('avoid') || this.avoid;
    },

    reveal() {
      if (!this.opened) this.opened = true;
    },

    /* ---------- helpers: text/array conversions ---------- */
    _strToUint8(str) {
      return new TextEncoder().encode(str);
    },
    _uint8ToStr(buf) {
      return new TextDecoder().decode(buf);
    },

    _toBase64Url(arrayBuffer) {
      const b64 = btoa(String.fromCharCode(...new Uint8Array(arrayBuffer)));
      return b64.replace(/\+/g, '-').replace(/\//g, '_').replace(/=+$/, '');
    },
    _fromBase64Url(b64url) {
      const b64 = b64url.replace(/-/g, '+').replace(/_/g, '/');
      // pad
      const pad = b64.length % 4 === 0 ? '' : '='.repeat(4 - (b64.length % 4));
      const raw = atob(b64 + pad);
      const arr = new Uint8Array(raw.length);
      for (let i = 0; i < raw.length; ++i) arr[i] = raw.charCodeAt(i);
      return arr.buffer;
    },

    legacyB64decode(val) {
      if (!val) return '';
      try {
        const std = val.replace(/-/g, '+').replace(/_/g, '/');
        const pad = std.length % 4 === 0 ? std : std + '='.repeat(4 - (std.length % 4));
        return decodeURIComponent(escape(window.atob(pad)));
      } catch (e) {
        return val;
      }
    },

    /* ---------- Encryption helpers ---------- */
    async _deriveKeyFromPassword(password, salt) {
      // password: string, salt: Uint8Array
      const pwKey = await window.crypto.subtle.importKey(
        'raw',
        this._strToUint8(password),
        { name: 'PBKDF2' },
        false,
        ['deriveKey']
      );
      const derived = await window.crypto.subtle.deriveKey(
        {
          name: 'PBKDF2',
          salt,
          iterations: 100000,
          hash: 'SHA-256'
        },
        pwKey,
        { name: 'AES-GCM', length: 256 },
        false,
        ['encrypt', 'decrypt']
      );
      return derived;
    },

    async encryptJson(obj) {
      try {
        const json = JSON.stringify(obj);
        const data = this._strToUint8(json);

        // create salt and iv
        const salt = window.crypto.getRandomValues(new Uint8Array(16)); // 16 bytes salt
        const iv = window.crypto.getRandomValues(new Uint8Array(12));   // 12 bytes iv for AES-GCM

        const key = await this._deriveKeyFromPassword(this.encryptionPass, salt);

        const ct = await window.crypto.subtle.encrypt(
          { name: 'AES-GCM', iv },
          key,
          data
        );

        // Compose token: version(1) + salt(16) + iv(12) + ciphertext
        const version = new Uint8Array([0x01]);
        const bufSalt = salt;
        const bufIv = iv;
        const bufCt = new Uint8Array(ct);

        const total = new Uint8Array(1 + bufSalt.length + bufIv.length + bufCt.length);
        let offset = 0;
        total.set(version, offset); offset += 1;
        total.set(bufSalt, offset); offset += bufSalt.length;
        total.set(bufIv, offset); offset += bufIv.length;
        total.set(bufCt, offset);

        return this._toBase64Url(total.buffer);
      } catch (e) {
        console.error('encryptJson error', e);
        throw e;
      }
    },

    async decryptToken(token) {
      // returns parsed JSON object on success or null on failure
      try {
        const buf = this._fromBase64Url(token);
        const view = new Uint8Array(buf);

        if (view.length < 1 + 16 + 12 + 1) {
          // too small to be our encrypted format
          return null;
        }
        const version = view[0];
        if (version !== 0x01) return null;

        const salt = view.slice(1, 1 + 16);
        const iv = view.slice(1 + 16, 1 + 16 + 12);
        const ct = view.slice(1 + 16 + 12);

        const key = await this._deriveKeyFromPassword(this.encryptionPass, salt);
        const plainBuf = await window.crypto.subtle.decrypt(
          { name: 'AES-GCM', iv },
          key,
          ct.buffer
        );
        const jsonStr = this._uint8ToStr(new Uint8Array(plainBuf));
        return JSON.parse(jsonStr);
      } catch (e) {
        // likely wrong format or wrong password / tampered token
        // console.debug('decryptToken failed', e);
        return null;
      }
    },

    /* ---------- Token generation & copying (async) ---------- */
    async encodeToken(obj) {
      // encrypt payload; returns string token
      return await this.encryptJson(obj);
    },

    async generateLink() {
      if (!this.form.match.trim()) {
        alert('Please add a recipient name (match).');
        return;
      }
      try {
        const token = await this.encodeToken(this.cleanPayload(this.form));
        this.generatedUrl = `${window.location.origin}/?token=${token}`;
      } catch (e) {
        console.error('generateLink encryption failed', e);
        alert('Could not generate encrypted link — falling back to unencrypted token.');
        // fallback to legacy base64
        const json = JSON.stringify(this.cleanPayload(this.form));
        const b64 = btoa(unescape(encodeURIComponent(json))).replace(/\+/g, '-').replace(/\//g, '_').replace(/=+$/, '');
        this.generatedUrl = `${window.location.origin}/?token=${b64}`;
      }
    },

    async copyGeneratedLink() {
      if (!this.generatedUrl) return;
      try {
        await navigator.clipboard.writeText(this.generatedUrl);
        this.flashCopied();
      } catch {
        alert('Could not copy link.');
      }
    },

    async generateAndCopyLink() {
      await this.generateLink();
      if (!this.generatedUrl) return;
      try {
        await navigator.clipboard.writeText(this.generatedUrl);
        this.flashCopied();
      } catch { /* noop */ }
    },

    flashCopied() {
      this.copiedFlash = true;
      window.setTimeout(() => (this.copiedFlash = false), 1200);
    },

    /* ---------- Magical Snow (no transform conflicts) ---------- */
    initSnow() {
      const container = this.$refs.snowLayer;
      if (!container) return;

      container.innerHTML = '';

      const vw = Math.max(document.documentElement.clientWidth || 0, window.innerWidth || 0);
      const vh = Math.max(document.documentElement.clientHeight || 0, window.innerHeight || 0);

      const baseCount = 90;
      const density = Math.min(240, Math.round(baseCount + (vw * vh) / 42000));

      const pickLayer = () => {
        const r = Math.random();
        if (r < 0.2) return 'near';
        if (r < 0.65) return 'mid';
        return 'far';
      };

      for (let i = 0; i < density; i++) {
        const flake = document.createElement('span');
        flake.className = 'flake';

        const layer = pickLayer();
        flake.dataset.layer = layer;

        const size =
          layer === 'near' ? (Math.random() * 7 + 4) :
          layer === 'mid'  ? (Math.random() * 5 + 3) :
                             (Math.random() * 3 + 1.5);

        const startX = Math.random() * 100;
        const delay = Math.random() * 12;
        const fall =
          layer === 'near' ? (6 + Math.random() * 8) :
          layer === 'mid'  ? (9 + Math.random() * 10) :
                             (12 + Math.random() * 12);

        const drift =
          layer === 'near' ? (4 + Math.random() * 5) :
          layer === 'mid'  ? (5 + Math.random() * 7) :
                             (7 + Math.random() * 9);

        const twinkle =
          layer === 'near' ? (2.6 + Math.random() * 2.2) :
          layer === 'mid'  ? (3.6 + Math.random() * 2.6) :
                             (4.6 + Math.random() * 3.2);

        const opacity =
          layer === 'near' ? (0.85 + Math.random() * 0.15) :
          layer === 'mid'  ? (0.6 + Math.random() * 0.25) :
                             (0.45 + Math.random() * 0.2);

        const aspect = 0.9 + Math.random() * 0.25;

        flake.style.setProperty('--flake-size', `${size.toFixed(2)}px`);
        flake.style.setProperty('--flake-left', `${startX.toFixed(2)}vw`);
        flake.style.setProperty('--flake-delay', `${delay.toFixed(2)}s`);
        flake.style.setProperty('--flake-fall', `${fall.toFixed(2)}s`);
        flake.style.setProperty('--flake-drift', `${drift.toFixed(2)}s`);
        flake.style.setProperty('--flake-twinkle', `${twinkle.toFixed(2)}s`);
        flake.style.setProperty('--flake-opacity', opacity.toFixed(2));
        flake.style.setProperty('--flake-aspect', aspect.toFixed(2));
        const glow = 0.25 + Math.random() * 0.35;
        flake.style.setProperty('--flake-glow', glow.toFixed(2));

        container.appendChild(flake);
      }
    },

    cleanPayload(payload) {
      const allowed = ['match', 'note', 'luxury', 'interests', 'comfort', 'nicheLink', 'avoid'];
      const out = {};
      for (const k of allowed) {
        if (payload[k]) out[k] = String(payload[k]).trim();
      }
      return out;
    }
  }
};
</script>

<style>
@import url('https://fonts.googleapis.com/css2?family=Great+Vibes&family=Poppins:wght@400;600&display=swap');

:root {
  --red-500: rgb(211, 22, 42);
  --red-500a: rgba(211, 22, 42, 0.3);
  --red-650: #b11223;
  --red-700: #930f1d;
  --black-900: #0a0a0a;
  --black-800: #141414;
  --white: #fff;
  --glass: rgba(255,255,255,0.10);
  --glass-border: rgba(255,255,255,0.22);
  --glass-strong: rgba(20,20,20,0.7);
  --muted: rgba(255,255,255,.7);
}

html, body, #app { height: 100%; }

body {
  margin: 0;
  font-family: 'Poppins', sans-serif;
  color: var(--white);
  background-image: url('./assets/background.png');
  background-size: cover;
  background-position: center;
  background-attachment: fixed;
  overflow-x: hidden;
}

.app::before{
  content: '';
  position: fixed; inset: 0; pointer-events: none; z-index: 0;
  background:
    radial-gradient(120% 70% at 50% 0%, rgba(0,0,0,.45), transparent 60%),
    linear-gradient(180deg, rgba(0,0,0,.30), rgba(0,0,0,.78));
}

/* Put ALL main UI above the snow layer */
.page{ position: relative; z-index: 5; min-height: 100vh; display: flex; flex-direction: column; }

/* -------- Full-width header (improved) -------- */
.header-bar {
  width: 100%;
  position: sticky;
  top: 0;
  background:
    linear-gradient(180deg, rgba(20,20,20,0.65), rgba(20,20,20,0.35)),
    radial-gradient(120% 80% at 50% -30%, rgba(211,22,42,0.12), rgba(0,0,0,0) 60%);
  border-bottom: 1px solid rgba(255,255,255,0.14);
  box-shadow: 0 8px 28px rgba(0, 0, 0, 0.55);
  backdrop-filter: blur(10px) saturate(115%);
  z-index: 10;
}
.header-bar__content {
  max-width: 1000px; margin: 0 auto; padding: 1.1rem 1rem 1.2rem; text-align: center;
  position: relative;
}
.header-bar__title{
  margin: .5rem 0 0;
  font-family: 'Great Vibes', cursive;
  font-size: clamp(2.4rem, 3.8vw, 3.4rem);
  color: var(--red-500);
  letter-spacing: .6px;
  text-shadow: 0 2px 12px rgba(0,0,0,.8);
}
.header-bar__sparkle{
  height: 2px; width: 180px; margin: .5rem auto .4rem;
  background: linear-gradient(90deg, transparent, rgba(255,255,255,0.65), transparent);
  filter: drop-shadow(0 0 6px rgba(255,255,255,.45));
  border-radius: 999px;
}
.header-bar__subtitle{
  margin: 0.25rem 0 0; opacity: 0.92; font-size: 1.06rem;
}

/* -------- Layout -------- */
.giftzone{ flex: 1; display: flex; align-items: center; justify-content: center; z-index: 100;}

/* ====== Form styles (refined) ====== */
.formzone {
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: start;
  min-height: calc(100vh - 160px);
  padding: 2rem 1rem 3rem;
  text-align: left;
}

.generator { width: min(900px, 95vw); }
.generator--card{
  position: relative;
  z-index: 6; /* keep card, inputs, buttons above snow */
  background: rgba(20, 20, 20, 0.7);
  border: 1px solid rgba(255, 255, 255, 0.1);
  border-radius: 16px;
  padding: 1.2rem 8rem 1.4rem;
  box-shadow: 0 10px 40px rgba(0, 0, 0, 0.4);
  backdrop-filter: blur(10px);
}

/* Card head */
.card-head{
  display: flex; align-items: center; justify-content: space-between;
  padding: .2rem .2rem 1rem .2rem; gap: 1rem; border-bottom: 1px solid rgba(255,255,255,.08);
  margin-bottom: 1rem;
}
.card-head__eyebrow{
  font-size: .85rem; opacity: .8; letter-spacing: .08em; text-transform: uppercase;
}
.card-head__title{
  margin: .2rem 0 0; font-size: 1.25rem; font-weight: 700; letter-spacing: .2px;
}
.card-head__right{ display: flex; gap: .5rem; flex-wrap: wrap; }
.tag{
  padding: .25rem .55rem; border-radius: 999px; font-size: .85rem; font-weight: 600;
  background: rgba(255,255,255,.06); border: 1px solid rgba(255,255,255,.16);
}
.tag--frost{ background: rgba(211,22,42,.14); border-color: rgba(211,22,42,.32); }

/* Section blocks */
.section{ display: grid; gap: .9rem; margin-bottom: 1rem; }
.section__title{
  font-weight: 700; letter-spacing: .2px; opacity: .95; display: flex; align-items: center; gap: .5rem;
}
.section__title .dot{
  width: 8px; height: 8px; border-radius: 999px;
  background: radial-gradient(circle at 50% 40%, var(--red-500), #7a0f1a);
  box-shadow: 0 0 8px rgba(211,22,42,.5);
}

/* Responsive form grid */
.form-grid{
  display: grid;
  grid-template-columns: 1fr 1fr;
  gap: 1rem 1.2rem;
  align-items: start;
}
.form-grid--balanced{
  grid-template-columns: repeat(2, minmax(0, 1fr));
}
@media (max-width: 760px) {
  .form-grid, .form-grid--balanced { grid-template-columns: 1fr; }
}

label {
  font-weight: 600;
  color: var(--red-500);
  margin-bottom: 0.35rem;
  display: flex;
  align-items: baseline;
  gap: .35rem;
}
label .req{
  font-size: .9rem;
  color: rgba(255,255,255,.85);
  background: rgba(255,255,255,.08);
  border: 1px solid rgba(255,255,255,.12);
  padding: 0 .35rem;
  border-radius: 6px;
}

.help, .small { color: var(--muted); font-size: .9rem; }
.small { margin: .25rem 0 0; }

input,
textarea {
  width: 100%;
  background: rgba(255, 255, 255, 0.08);
  color: white;
  border: 1px solid rgba(255, 255, 255, 0.15);
  border-radius: 12px;
  padding: 0.75rem 0.95rem;
  font-size: 1rem;
  resize: none;
  transition: border-color .15s ease, box-shadow .15s ease, background .15s ease, transform .02s ease;
}
input:hover, textarea:hover { background: rgba(255,255,255,.10); }
input:active, textarea:active { transform: translateY(0.5px); }

input:focus,
textarea:focus {
  outline: none;
  border-color: var(--red-500);
  box-shadow: 0 0 0 3px rgba(211,22,42,.18), 0 0 10px var(--red-500a);
  background: rgba(255, 255, 255, 0.12);
}

/* Actions */
.actions{
  display: flex; gap: .6rem; justify-content: space-between; margin-top: .9rem;
}
.actions--stack{
 margin-top: 2.5%;
}
.actions__row{
  display: flex; gap: .6rem; justify-content: flex-end; flex-wrap: wrap;
}

.btn-generate, .btn-secondary{
  border: none;
  padding: 0.9rem 1.3rem;
  border-radius: 12px;
  font-size: 1rem;
  font-weight: 700;
  cursor: pointer;
  transition: background 0.2s ease, opacity .2s ease, transform .05s ease, box-shadow .2s ease;
}
.btn-generate--large, .btn-secondary--large{ padding: 1rem 1.4rem; }
.btn-generate {
  background: var(--red-500);
  color: white;
  box-shadow: 0 8px 20px rgba(211,22,42,.25);
}
.btn-generate:hover { background: var(--red-650); }
.btn-generate:active { transform: translateY(1px); }
.btn-secondary{
  background: rgba(255,255,255,.08); color: white; border: 1px solid rgba(255,255,255,.18);
}
.btn-secondary:hover{ opacity: .95; }
.btn-secondary:active { transform: translateY(1px); }

.output {
  margin-top: .2rem;
  text-align: center;
  word-break: break-all;
}
.output h3 { margin: 0.5rem 0 0.35rem; }
.output__actions{ margin-top: .5rem; display: flex; justify-content: center; }
.copied-flash{
  margin-top: .45rem;
  font-size: .95rem;
  opacity: .95;
}

/* -------- Gift box (kept for reveal page) -------- */
.box{
  position: relative; transform: scale(1.06);
  transition: transform .3s ease, filter .3s ease;
}
.box:hover{ transform: scale(1.12); filter: drop-shadow(0 18px 40px rgba(0,0,0,.45)); }
.box::before{
  content: ""; width: 520px; height: 520px; position: absolute; z-index: -1;
  top: 50%; left: 50%; transform: translate(-50%, -50%); border-radius: 50%;
  background: radial-gradient(circle at 50% 50%, rgba(211,22,42,.25), rgba(10,10,10,0) 60%);
}
.box-body{
  position: relative; height: 240px; width: 270px; margin-top: 110px;
  border-bottom-left-radius: 6%; border-bottom-right-radius: 6%;
  box-shadow: 0px 12px 30px rgba(0,0,0,0.55);
  background: linear-gradient(#1a1a1a, #0c0c0c);
}
.box-body::after{
  content: ""; position: absolute; top: 0; bottom: 0; left: 50%;
  transform: translateX(-50%); width: 64px; background: linear-gradient(var(--red-500), var(--red-700));
}
.box-lid{
  position: absolute; z-index: 1; left: 50%; transform: translateX(-50%);
  bottom: 88%; height: 52px; width: 290px; border-radius: 6%;
  background: linear-gradient(#1f1f1f, #0e0e0e); box-shadow: 0 14px 20px -6px rgba(0,0,0,0.6);
}
.box-lid::after{
  content: ""; position: absolute; top: 0; bottom: 0; left: 50%; transform: translateX(-50%);
  width: 64px; background: linear-gradient(var(--red-650), var(--red-700));
}
.box-bowtie{ z-index: 1; height: 100%; }
.box-bowtie::before, .box-bowtie::after{
  content: ""; width: 92px; height: 92px; border: 16px solid var(--red-500);
  border-radius: 50% 50% 0 50%; position: absolute; bottom: 99%; z-index: -1;
  box-shadow: 0 6px 16px rgba(211,22,42,.35);
}
.box-bowtie::before{ left: 50%; transform: translateX(-100%) skew(10deg, 10deg); }
.box-bowtie::after { left: 50%; transform: translateX(0%) rotate(90deg) skew(10deg, 10deg); }

.box.opened .box-body{ animation: box-body 1s forwards ease-in-out; }
.box.opened .box-lid { animation: box-lid 1s forwards ease-in-out; }
.box.opened .box-bowtie::before{ animation: box-bowtie-left 1.1s forwards ease-in-out; }
.box.opened .box-bowtie::after { animation: box-bowtie-right 1.1s forwards ease-in-out; }

@keyframes box-lid {
  0%,42% { transform: translate3d(-50%,0%,0) rotate(0deg); }
  60% { transform: translate3d(-85%,-230%,0) rotate(-25deg); }
  90%,100% { transform: translate3d(-119%,225%,0) rotate(-70deg); }
}
@keyframes box-body {
  0% { transform: translate3d(0%,0%,0) rotate(0deg); }
  25% { transform: translate3d(0%,25%,0) rotate(20deg); }
  50% { transform: translate3d(0%,-15%,0) rotate(0deg); }
  70% { transform: translate3d(0%,0%,0) rotate(0deg); }
}
@keyframes box-bowtie-right {
  0%,50%,75% { transform: translateX(0%) rotate(90deg) skew(10deg, 10deg); }
  90%,100% { transform: translate(-50%,-15%) rotate(45deg) skew(10deg, 10deg); }
}
@keyframes box-bowtie-left {
  0% { transform: translateX(-100%) rotate(0deg) skew(10deg, 10deg); }
  50%,75% { transform: translate(-50%,-15%) rotate(45deg, 10deg); }
  90%,100% { transform: translateX(-100%) rotate(0deg) skew(10deg, 10deg); }
}

/* =================== POPUP (modal) =================== */
.reveal-overlay{
  position: fixed; inset: 0; z-index: 110;
  display: grid; place-items: center;
  background: rgba(0,0,0,.55);
  backdrop-filter: blur(2px);
}
.reveal-card{
  position: relative;
  width: min(820px, 92vw);
  padding: clamp(22px, 3vw, 30px) clamp(22px, 4vw, 36px);
  text-align: center; color: var(--white);
  background:
    radial-gradient(120% 140% at 50% -10%, rgba(211,22,42,0.14), rgba(0,0,0,0) 45%),
    linear-gradient(180deg, rgba(255,255,255,0.10), rgba(255,255,255,0.08));
  border: 1px solid rgba(255,255,255,0.2);
  border-radius: 20px;
  box-shadow: 0 30px 100px rgba(0,0,0,.55), inset 0 1px 0 rgba(255,255,255,0.3);
  backdrop-filter: blur(10px) saturate(120%);
}
.reveal-card::before{
  content: ""; position: absolute; inset: -2px; border-radius: 22px;
  background: radial-gradient(80% 60% at 50% 0%, rgba(211,22,42,0.35), transparent 60%);
  z-index: -1;
}
.reveal-card__sparkle{
  height: 2px; width: 180px; margin: .4rem auto .2rem;
  background: linear-gradient(90deg, transparent, rgba(255,255,255,0.65), transparent);
  filter: drop-shadow(0 0 6px rgba(255,255,255,.45));
  border-radius: 999px;
}
.reveal-card__eyebrow{ margin-bottom: .15rem; }
.header-like{
  font-family: 'Great Vibes', cursive;
  font-size: clamp(2.4rem, 3.8vw, 3.4rem);
  color: var(--red-500);
  letter-spacing: .6px;
  text-shadow: 0 2px 12px rgba(0,0,0,.8);
}
.reveal-card__name{
  font-family: 'Poppins', sans-serif;
  font-weight: 400;
  font-size: 2.6rem;
  line-height: 1.05; margin-top: .25rem;
  text-shadow: 0 0 26px rgba(211,22,42,.85);
  color: var(--red-500);
  margin-top: 1rem;
}
.reveal-card__note{ margin-top: .6rem; opacity: .92; font-size: 1.06rem; }

 /* Extras */
.extras{ margin-top: 1.1rem; text-align: left; }
.extras__title{
  text-align: center; margin: .4rem 0 1rem 0; font-weight: 600; letter-spacing: .3px;
  color: var(--white); opacity: .95;
}
.extras__grid{ display: grid; grid-template-columns: 1fr; gap: .75rem; }
.extras__grid--two{ grid-template-columns: repeat(2, minmax(0, 1fr)); }
.extras__row--full{ grid-column: 1 / -1; }
@media (max-width: 760px){ .extras__grid--two{ grid-template-columns: 1fr; } }
.extras__row{
  display: grid; gap: .35rem;
  background: rgba(255,255,255,0.06);
  border: 1px solid rgba(255,255,255,0.14);
  border-radius: 12px;
  padding: .7rem .85rem;
}
.extras__label{ font-size: .9rem; opacity: .9; color: rgba(255,255,255,0.9); }
.extras__value{ font-size: 1.05rem; line-height: 1.45; }
.extras__value a{ color: var(--red-500); text-decoration: none; border-bottom: 1px dashed var(--red-500); }
.extras__value a:hover{ opacity: .9; }
.extras__value--muted{ opacity: .8; font-style: italic; }

/* Popup transition */
.pop-reveal-enter-active, .pop-reveal-leave-active{ transition: opacity .28s ease, transform .28s ease; }
.pop-reveal-enter-from, .pop-reveal-leave-to{ opacity: 0; transform: scale(.96); }

/* =================== Magical Snow (behind the UI now) =================== */
.snow{
  position: fixed; inset: 0; pointer-events: none;
  z-index: 1; /* BELOW the page (z=5) so it won’t sit on inputs */
  overflow: hidden;
}

/* CSS-only fallback so you always see *some* snow even if JS fails. */
.snow--fallback,
.snow--fallback::before,
.snow--fallback::after{
  position: fixed; inset: 0; content: ''; pointer-events: none;
  background-image:
    radial-gradient(2px 2px at 20px 30px, rgba(255,255,255,.85) 50%, transparent 51%),
    radial-gradient(3px 3px at 60px 80px, rgba(255,255,255,.85) 50%, transparent 51%),
    radial-gradient(2px 2px at 120px 40px, rgba(255,255,255,.85) 50%, transparent 51%);
  background-size: 220px 220px;
  animation: snow-fallback 14s linear infinite;
  opacity: .35;
}
.snow--fallback::before{ animation-duration: 20s; opacity: .35; filter: blur(1px); }
.snow--fallback::after { animation-duration: 28s; opacity: .25; filter: blur(2px); }
@keyframes snow-fallback { from { background-position: 0 -220px; } to { background-position: 0 220px; } }

/* JS flakes */
.flake{
  position: absolute;
  top: -12px;
  left: var(--flake-left, 0);
  width: calc(var(--flake-size) * var(--flake-aspect, 1));
  height: var(--flake-size);
  border-radius: 50%;
  background:
    radial-gradient(circle at 35% 35%, #ffffff 0%, #ffffff 60%, rgba(255,255,255,.92) 61%, rgba(255,255,255,.8) 100%);
  box-shadow:
    0 0 6px rgba(255,255,255, calc(var(--flake-glow, .3))),
    0 0 12px rgba(255,255,255, calc(var(--flake-glow, .3) * .8));
  opacity: var(--flake-opacity, .9);
  filter: drop-shadow(0 0 2px rgba(255,255,255,.35));
  transform: translate3d(0, -12vh, 0);
  will-change: transform, opacity, filter;
  animation:
    snow-fall var(--flake-fall, 12s) linear var(--flake-delay, 0s) infinite,
    snow-drift var(--flake-drift, 6s) ease-in-out var(--flake-delay, 0s) infinite alternate,
    snow-twinkle var(--flake-twinkle, 4s) ease-in-out var(--flake-delay, 0s) infinite;
}

/* Depth-based parallax via dataset layer */
.flake[data-layer="far"]{
  opacity: calc(var(--flake-opacity, .8) * .8);
  filter: blur(0.6px) drop-shadow(0 0 1px rgba(255,255,255,.2));
  z-index: 1;
}
.flake[data-layer="mid"]{ z-index: 2; }
.flake[data-layer="near"]{
  opacity: min(1, calc(var(--flake-opacity, .95) + .05));
  filter: drop-shadow(0 0 3px rgba(255,255,255,.45));
  z-index: 4;
}

/* Animations (no transform conflicts) */
@keyframes snow-fall {
  0%   { transform: translate3d(0, -12vh, 0); }
  100% { transform: translate3d(0, 110vh, 0); }
}
@keyframes snow-drift {
  0%   { margin-left: -14px; }
  100% { margin-left:  14px; }
}
@keyframes snow-twinkle {
  0%, 100% { opacity: calc(var(--flake-opacity, .9) * .92); }
  50%      { opacity: 1; }
}

/* --- /new form layout fixes (non-destructive overrides) --- */
.generator--card{
  padding: clamp(16px, 2.5vw, 24px) clamp(16px, 3.5vw, 28px) clamp(18px, 3vw, 24px);
}

/* Section spacing + ensure groups don’t collapse */
.section{ 
  display: grid; 
  gap: 0.9rem; 
  margin-bottom: 1rem; 
}
.form__group{ 
  display: flex; 
  flex-direction: column; 
  min-width: 0; 
}

/* Inputs: prevent any accidental overlap on very small widths */
input, textarea{
  line-height: 1.2;
  display: block;                 /* prevents inline quirks */
  width: 96%;
}

/* Buttons: consistent spacing regardless of viewport height */
.actions--stack{
  margin-top: 1rem;
}

/* Keep the card comfortably wide on desktop but not edge-to-edge */
.generator{
  width: min(860px, 95vw);
}

/* Extra insurance on tiny screens */
@media (max-width: 420px){
  .generator--card{ padding: 14px 14px 16px; }
  .header-bar__content{ padding-left: 14px; padding-right: 14px; }
}
</style>
